import React from 'react';
import { array, string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionPortfolio.module.css';
import { t } from '../../../util/messages';
import config from '../../../config/config';

/**
 * Selected work.
 *
 * A card grid rather than the previous carousel: all five projects are on the
 * page at once, so nothing is hidden behind an interaction, and the first
 * project gets a wide featured treatment. Cards are image-forward — the work
 * is the argument.
 */

const ArrowUpRight = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M4 11L11 4M11 4H5M11 4v6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Metric = ({ metric }) => (
  <div className={css.metric}>
    <span className={css.metricValue}>{metric.value}</span>
    <span className={css.metricLabel}>{metric.label}</span>
  </div>
);

const ProjectCard = ({ project, isFeatured, delay }) => (
  <a
    className={classNames(css.card, { [css.cardFeatured]: isFeatured })}
    href={project.link || '#portfolio'}
    aria-label={`${project.name} — ${project.title}`}
    data-reveal
    data-reveal-delay={delay}
  >
    <div className={css.media}>
      {/*
        Framing is per project, set in configPortfolio.js. Inline because
        these are content decisions about one specific image, not reusable
        style — see the IMAGE FRAMING notes in that file.
      */}
      <img
        className={css.image}
        src={project.image}
        alt={`${project.name} product screens`}
        width="900"
        height="675"
        loading="lazy"
        style={{
          objectPosition: project.imagePosition || undefined,
          objectFit: project.imageFit || undefined,
          '--image-scale': project.imageScale || undefined,
        }}
      />
      <span className={css.shade} aria-hidden="true" />
      <span className={css.openIcon} aria-hidden="true">
        <ArrowUpRight />
      </span>
    </div>

    <div className={css.body}>
      <div className={css.meta}>
        <span className={css.sector}>{project.sector}</span>
        <span className={css.client}>{project.name}</span>
      </div>

      <h3 className={classNames(css.cardTitle, { [css.cardTitleFeatured]: isFeatured })}>
        {project.title}
      </h3>
      <p className={css.cardDescription}>{project.description}</p>

      {project.metrics?.length > 0 && (
        <div className={css.metrics}>
          {project.metrics.map(metric => (
            <Metric key={metric.label} metric={metric} />
          ))}
        </div>
      )}

      {project.stack?.length > 0 && (
        <ul className={css.stack}>
          {project.stack.map(tech => (
            <li key={tech} className={css.tech}>
              {tech}
            </li>
          ))}
        </ul>
      )}
    </div>
  </a>
);

const SectionPortfolio = ({ rootClassName, className, projects: projectsProp }) => {
  const classes = classNames(rootClassName || css.root, className);
  const projects = projectsProp || config.portfolioConfig.configPortfolio;

  return (
    <section id="portfolio" className={classes} aria-label="Our portfolio">
      <div className={css.content}>
        <header className={css.header} data-reveal>
          <div>
            <span className={css.tag}>
              <i aria-hidden="true" />
              {t('SectionPortfolio.eyebrow')}
            </span>
            <h2 className={css.title}>{t('SectionPortfolio.heading')}</h2>
          </div>
          <p className={css.description}>{t('SectionPortfolio.description')}</p>
        </header>

        <div className={css.grid}>
          {projects.map((project, index) => (
            <ProjectCard
              /* By position when a page hands in a rotating subset, so the DOM
                 nodes are reused when the selection changes. */
              key={projectsProp ? index : project.id}
              project={project}
              isFeatured={Boolean(project.featured)}
              delay={index === 0 ? 0 : ((index - 1) % 2) * 80}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

SectionPortfolio.propTypes = {
  rootClassName: string,
  className: string,
  projects: array,
};

export default React.memo(SectionPortfolio);
